import { ExecuteFlowConfig } from '../classes/execute-flow/execute-flow-config';
import { Guid } from '../classes/guid';
import { MicrotaskType } from '../enums/microtask-types.enum';
import { ObjectState } from '../enums/objectState.enum';
import { TaskOverrideMapping } from '../interfaces/execute-flow/task-override-mapping';
import { MicroTaskConfigType } from '../types/micro-task-config-type';

/**
 * Ensures that all the tasks and task mappings on the sub flow are unique
 * by generating new IDs for the sub tasks and updating the task mappings accordingly.
 *
 * @param taskType - The type of the microtask.
 * @param objectState - The state of the object (Created, Modified, etc.).
 * @param config - The microtask configuration to be made unique.
 * @returns A new ExecuteFlowConfig with unique task IDs and updated mappings, or the original config if not applicable.
 */
export function makeExecuteFlowTaskUnique(
	taskType: MicrotaskType,
	config: MicroTaskConfigType,
	objectState?: ObjectState
) {
	if (
		taskType === MicrotaskType.ExecuteFlow &&
		(objectState === ObjectState.Created || objectState === ObjectState.Modified)
	) {
		const tempConfig = new ExecuteFlowConfig(config);

		const idSets: TaskOverrideMapping[] = [];
		tempConfig.subTaskConfigs.forEach(subTaskConfig => {
			const idSet: TaskOverrideMapping = {
				originalTaskID: subTaskConfig.id,
				overrideTaskID: Guid(),
				taskReferenceId: subTaskConfig.referenceId,
			};
			subTaskConfig.id = idSet.overrideTaskID;
			idSets.push(idSet);
		});
		for (const taskMapping of tempConfig.taskMappings) {
			for (const idSet of idSets) {
				if (taskMapping.sourceTaskID === idSet.originalTaskID) {
					taskMapping.sourceTaskID = idSet.overrideTaskID;
				}
				if (taskMapping.destinationTaskID === idSet.originalTaskID) {
					taskMapping.destinationTaskID = idSet.overrideTaskID;
				}
			}
		}
		tempConfig.taskOverrideMapping = idSets;

		return tempConfig;
	}
	return config;
}
